import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from 'pdfjs-dist';
// eslint-disable-next-line import/default -- Vite ?worker suffix provides default export
import PdfJsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';

import { Box, Button, Card, CardContent, CircularProgress, Divider, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut } from '@mui/icons-material';
import type { SxProps, Theme } from '@mui/material/styles';

type Props = {
  pdfData: Blob;
  fileName?: string;
};

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const SCALE_STEP = 1.2;

const containerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const stateContainerStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 384,
};

const stateCardContentStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 4,
};

const errorCardContentStyles: SxProps<Theme> = {
  p: 4,
  textAlign: 'center',
};

const toolbarStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 2,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const toolbarGroupStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const canvasScrollAreaStyles: SxProps<Theme> = {
  flex: 1,
  overflow: 'auto',
  bgcolor: 'action.hover',
  p: 2,
};

const canvasCenterStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
};

const canvasStyles: SxProps<Theme> = {
  boxShadow: 3,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'common.white',
  maxWidth: '100%',
  height: 'auto',
};

const renderingOverlayStyles: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'rgba(255, 255, 255, 0.8)',
  borderRadius: 1,
};

const pageNumberLabelStyles: SxProps<Theme> = {
  fontWeight: 500,
  px: 1,
};

const zoomLabelStyles: SxProps<Theme> = {
  fontWeight: 500,
  px: 1,
  minWidth: '4rem',
  textAlign: 'center',
};

export const PdfViewer = ({ pdfData, fileName = 'document.pdf' }: Props) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [pdf, setPdf] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerReady, setWorkerReady] = useState(false);

  const renderPage = useCallback(
    async (pageNum: number, currentScale: number) => {
      if (!pdf || !canvasRef.current) {
        return;
      }

      setRendering(true);
      try {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: currentScale });

        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }

        const context = canvas.getContext('2d');
        if (!context) {
          throw new Error('Failed to get canvas context');
        }

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
          canvasContext: context,
          viewport,
          canvas,
        }).promise;
      } catch {
        setError(t('pdfViewer.renderFailed'));
      } finally {
        setRendering(false);
      }
    },
    [pdf, t]
  );

  useEffect(() => {
    try {
      const worker = new PdfJsWorker();
      pdfjsLib.GlobalWorkerOptions.workerPort = worker;
      setWorkerReady(true);
    } catch {
      setError(t('pdfViewer.initFailed'));
    }
  }, [t]);

  useEffect(() => {
    const loadPdf = async () => {
      if (!workerReady || !pdfData) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const arrayBuffer = await pdfData.arrayBuffer();
        const pdfDoc = await pdfjsLib.getDocument({
          data: arrayBuffer,
          useSystemFonts: true,
        }).promise;

        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setCurrentPage(1);
      } catch {
        setError(t('pdfViewer.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadPdf();
  }, [pdfData, workerReady, t]);

  useEffect(() => {
    if (pdf && currentPage && canvasRef.current) {
      renderPage(currentPage, scale);
    }
  }, [pdf, currentPage, scale, renderPage]);

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev * SCALE_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev / SCALE_STEP, MIN_SCALE));
  }, []);

  const handleDownload = useCallback(() => {
    const url = URL.createObjectURL(pdfData);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [pdfData, fileName]);

  if (loading) {
    return (
      <Box sx={stateContainerStyles}>
        <Card>
          <CardContent sx={stateCardContentStyles}>
            <CircularProgress size={32} sx={{ mr: 1 }} />
            <Typography component="span">{t('pdfViewer.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={stateContainerStyles}>
        <Card>
          <CardContent sx={errorCardContentStyles}>
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button onClick={handleDownload} variant="outlined" startIcon={<Download />}>
              {t('pdfViewer.downloadInstead')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={toolbarStyles}>
        <Box sx={toolbarGroupStyles}>
          <Button variant="outlined" size="small" onClick={handlePrevPage} disabled={currentPage <= 1 || rendering}>
            <ChevronLeft />
          </Button>

          <Typography variant="body2" sx={pageNumberLabelStyles}>
            {currentPage} / {totalPages}
          </Typography>

          <Button
            variant="outlined"
            size="small"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages || rendering}
          >
            <ChevronRight />
          </Button>
        </Box>

        <Box sx={toolbarGroupStyles}>
          <Button variant="outlined" size="small" onClick={handleZoomOut} disabled={scale <= MIN_SCALE || rendering}>
            <ZoomOut />
          </Button>

          <Typography variant="body2" sx={zoomLabelStyles}>
            {Math.round(scale * 100)}%
          </Typography>

          <Button variant="outlined" size="small" onClick={handleZoomIn} disabled={scale >= MAX_SCALE || rendering}>
            <ZoomIn />
          </Button>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          <Button variant="outlined" size="small" onClick={handleDownload}>
            <Download />
          </Button>
        </Box>
      </Box>

      <Box sx={canvasScrollAreaStyles}>
        <Box sx={canvasCenterStyles}>
          <Box sx={{ position: 'relative' }}>
            <Box
              component="canvas"
              ref={canvasRef}
              sx={canvasStyles}
              style={{ display: rendering ? 'none' : 'block' }}
            />
            {rendering && (
              <Box sx={renderingOverlayStyles}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
